import { S3Client, ObjectCannedACL } from "@aws-sdk/client-s3";
import { Upload } from "@aws-sdk/lib-storage";

const keyString: string = "T2093989N3939912BU353588877DDYWC"; // 32-byte key (256-bit)
const encoder = new TextEncoder();
const decoder = new TextDecoder();

const secretKey: string = '12345678901234567890123456789012';

const REGION = process.env.REACT_APP_AWS_REGION // e.g. 'ap-south-1'
const BUCKET_NAME = process.env.REACT_APP_AWS_BUCKET_NAME;

const s3Client = new S3Client({
    region: REGION,
    credentials: {
        accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY!,
        secretAccessKey: process.env.REACT_APP_AWS_SECRET_KEY!,
    },
});


async function getKey(): Promise<CryptoKey> {
    // Convert string to ArrayBuffer
    const keyData: Uint8Array = encoder.encode(keyString);
    return crypto.subtle.importKey(
        "raw",
        keyData,
        { name: "AES-GCM" },
        false,
        ["encrypt", "decrypt"]
    );
}

// Encrypt
export async function encrypt(message: string): Promise<string> {
    try {
        const key: CryptoKey = await getKey();
        const iv: Uint8Array = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV for GCM
        const encodedMessage: Uint8Array = encoder.encode(message);

        const encrypted: ArrayBuffer = await crypto.subtle.encrypt(
            { name: "AES-GCM", iv },
            key,
            encodedMessage
        );

        // Combine IV + Ciphertext
        const combined = new Uint8Array(iv.length + encrypted.byteLength);
        combined.set(iv, 0);
        combined.set(new Uint8Array(encrypted), iv.length);

        return btoa(String.fromCharCode(...combined)); // Base64
    } catch (err) {
        console.error("Encryption failed:", err);
        return message;
    }
}

// Decrypt
export async function decrypt(base64String: string): Promise<string> {
    // If empty or null, return empty string
    if (!base64String || base64String.trim() === '') {
        return '';
    }

    // Common plain text messages that shouldn't be decrypted
    if (base64String === "Delete this message..." || base64String.includes(' ')) {
        return base64String;
    }

    try {
        const key: CryptoKey = await getKey();
        
        // Attempt to decode base64
        let combined: Uint8Array;
        try {
            combined = Uint8Array.from(
                atob(base64String),
                (c) => c.charCodeAt(0)
            );
        } catch (atobErr) {
            // Not valid base64, must be plain text
            return base64String;
        }

        // AES-GCM combined data must be at least 13 bytes (12 bytes IV + at least 1 byte ciphertext)
        if (combined.length < 13) {
            return base64String;
        }

        // Extract IV and ciphertext
        const iv: Uint8Array = combined.slice(0, 12);
        const ciphertext: Uint8Array = combined.slice(12);

        const decrypted: ArrayBuffer = await crypto.subtle.decrypt(
            { name: "AES-GCM", iv },
            key,
            ciphertext
        );

        return decoder.decode(decrypted);
    } catch (err) {
        // Only log if it really looks like it should have worked but didn't
        // (e.g. valid base64 of correct length but wrong key)
        if (base64String.length > 20 && !base64String.includes(' ')) {
            console.warn("Decryption failed for suspected encrypted message:", base64String.substring(0, 50) + "...");
        }
        return base64String;
    }
}

// utils/decryptProfileDetail.ts
export async function decryptProfileDetail(encrypted: string) {
    // Remove quotes if stored as JSON string
    encrypted = encrypted.replace(/^"|"$/g, '');

    const [ivBase64, encryptedData] = encrypted.split(':');

    // Trim any whitespace
    const ivB64 = ivBase64.trim();
    const ctB64 = encryptedData.trim();

    // Convert Base64 to Uint8Array
    const iv = Uint8Array.from(atob(ivB64), (c) => c.charCodeAt(0));
    const ciphertext = Uint8Array.from(atob(ctB64), (c) => c.charCodeAt(0));

    // Encode the key
    const keyBytes = new TextEncoder().encode(secretKey);

    // Import AES key
    const key = await crypto.subtle.importKey(
        'raw',
        keyBytes,
        { name: 'AES-CBC' },
        false,
        ['decrypt']
    );

    // Decrypt
    const decryptedBuffer = await crypto.subtle.decrypt(
        { name: 'AES-CBC', iv },
        key,
        ciphertext
    );

    const decryptedText = new TextDecoder().decode(decryptedBuffer);

    return JSON.parse(decryptedText);
}

export const uploadFile = async (file: File) => {
    const filePath = `tNbSuite/Media/User/Images/${file?.name}`;
    try {
        const upload = new Upload({
            client: s3Client,
            params: {
                Bucket: BUCKET_NAME!,
                Key: `${filePath}`,
                Body: file,
                ContentType: file.type,
                ACL: "public-read" as ObjectCannedACL,
            },
        });

        const result = await upload.done();
        return `https://s3.amazonaws.com/${filePath}`;
    } catch (err) {
        console.error("Error uploading file:", err);
        throw err;
    }
};


export default function chatTimeAgo(dateString: string | undefined | null): string {
    // Handle null, undefined, or empty string
    if (!dateString || typeof dateString !== 'string') {
        return "Just now";
    }

    // Parse the date string (format: "dd-MM-yyyy HH:mm:ssZ")
    const parts = dateString.split(" ");
    if (parts.length < 2) return "Just now";

    const [datePart, timePartWithZone] = parts;
    if (!datePart || !timePartWithZone) return "Just now";

    const dateParts = datePart.split("-");
    if (dateParts.length !== 3) return "Just now";

    const [day, month, year] = dateParts.map(Number);
    if (isNaN(day) || isNaN(month) || isNaN(year)) return "Just now";

    const timeMatch = timePartWithZone.match(/(\d{2}:\d{2}:\d{2})(.*)/);
    if (!timeMatch) return "Just now";

    const [_, timePart] = timeMatch;
    const timeParts = timePart.split(":");
    if (timeParts.length !== 3) return "Just now";

    const [hour, minute, second] = timeParts.map(Number);
    if (isNaN(hour) || isNaN(minute) || isNaN(second)) return "Just now";

    const date = new Date(year, month - 1, day, hour, minute, second);
    if (isNaN(date.getTime())) return "Just now";

    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    // Handle future dates
    if (diffInSeconds < 0) return "Just now";

    const years = Math.floor(diffInSeconds / (3600 * 24 * 365));
    if (years > 0) return years === 1 ? "1 year ago" : `${years} years ago`;

    const months = Math.floor(diffInSeconds / (3600 * 24 * 30));
    if (months > 0) return months === 1 ? "1 month ago" : `${months} months ago`;

    const days = Math.floor(diffInSeconds / (3600 * 24));
    if (days > 0) return days === 1 ? "1 day ago" : `${days} days ago`;

    const hours = Math.floor(diffInSeconds / 3600);
    if (hours > 0) return hours === 1 ? "1 hour ago" : `${hours} hours ago`;

    const minutes = Math.floor(diffInSeconds / 60);
    if (minutes > 0) return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;

    const seconds = diffInSeconds;
    if (seconds > 0) return seconds === 1 ? "1 second ago" : `${seconds} seconds ago`;

    return "Just now";
}


export const formatDate = (date: Date | string): string => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
};

export const debounce = <T extends (...args: any[]) => any>(
    func: T,
    wait: number
): ((...args: Parameters<T>) => void) => {
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
};

export const capitalize = (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const generateId = (): string => {
    return Math.random().toString(36).substr(2, 9);
};

export const generateImage = (
  text: string,
  fontSize = 30,
  fontFamily = "Inter, sans-serif"
) => {
  const initials = text
    ?.split(" ")
    .map(word => word.charAt(0).toUpperCase())
    .join("")
    .slice(0, 2);

  const svgString = `
    <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100">
      <rect width="100%" height="100%" fill="#6c757d"/>
      <text x="50%" y="50%" text-anchor="middle" dominant-baseline="middle"
        font-size="${fontSize}" font-family="${fontFamily}" fill="white">
        ${initials}
      </text>
    </svg>
  `;

  const encodedData = window.btoa(
    unescape(encodeURIComponent(svgString))
  );

  return `data:image/svg+xml;base64,${encodedData}`;
};